import { useContext, useEffect, useState } from 'react';
import { BaseBoard } from '~/components/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetCRMBoard } from '~/thunks/customer/customerThunk';
import { handleApiError } from '~/utils/helper';
import { Typography, Box } from '@mui/material';
import styles from './CRM.module.scss';
import classNames from 'classnames/bind';
import customerApi from '~/apis/customer';
import { ColumnType, ItemChangeProps } from '~/components/common/base-board/BaseBoard';
import { useTranslation } from 'react-i18next';
import CRMCard from '~/components/crm/CRMCard';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { useNavigate } from 'react-router-dom';
import { ConnectionStatusEnum } from '~/utils/enum';
import { adminRouteAbsolute } from '~/utils/constants/route';
import ModalAlertConfirm from '~/components/common/modal-alert-confirm/ModalAlertConfirm';
import { RingCentralContext } from '~/context/RingCentralContext';
const cx = classNames.bind(styles);

const CRM = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/customer']);
  const permission = usePermission();
  const { ringCentralStatus, excuteCall, sendSMS } = useContext(RingCentralContext);
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  const { filterBoard, needRefreshData, CRMBoard } = useAppSelector((state) => state.customer);

  //#endregion Selector

  //#region Declare State
  const [openDisconnectConfirm, setOpenDisconnectConfirm] = useState<boolean>(false);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    dispatch(handleGetCRMBoard({}))
      .unwrap()
      .catch((e: any) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterBoard, needRefreshData]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleItemChange = async (item: ItemChangeProps) => {
    try {
      await customerApi.updateFields(item.itemId, {
        crmPosition: item.itemPosition,
        crmStatusId: item.columnId,
      });
    } catch (e: any) {
      handleApiError(e);
    }
  };

  const renderColumnComponent = (data: ColumnType) => {
    return (
      <Typography variant='h6' gutterBottom className={cx('container-tittle')}>
        {data.name}
      </Typography>
    );
  };

  const handleNavigateSetting = async () => {
    navigate(adminRouteAbsolute.ringCentral);
  };

  const handleCall = async (phoneNumber: string) => {
    if (ringCentralStatus !== ConnectionStatusEnum.CONNECTED) {
      setOpenDisconnectConfirm(true);
      return;
    }

    excuteCall && excuteCall(phoneNumber);
  };

  const handleSendMessage = async (phoneNumber: string) => {
    if (ringCentralStatus !== ConnectionStatusEnum.CONNECTED) {
      setOpenDisconnectConfirm(true);
      return;
    }

    sendSMS && sendSMS(phoneNumber);
  };
  //#endregion Handle Function

  return (
    <>
      <Box className='flex items-center flex-1 mb-[20px]'>
        <Typography className='text-[32px]/[38.4px] font-normal'>{t('pipeline')}</Typography>
        {ringCentralStatus !== ConnectionStatusEnum.CONNECTED && (
          <Typography
            onClick={handleNavigateSetting}
            variant='subtitle1'
            className={cx('underline text-[#e8625a] bg-[#e8625a0d] p-[5px] rounded-[8px] ml-[20px]')}
          >
            {ringCentralStatus === ConnectionStatusEnum.CONNECTING
              ? t('CRM.ringCentralConnecting')
              : t('CRM.ringCentralNotConnected')}
          </Typography>
        )}
      </Box>
      <Box className={cx('JobBoardContainer')}>
        <BaseBoard
          data={CRMBoard}
          renderColumn={renderColumnComponent}
          renderItem={(item) => <CRMCard crm={item} onCall={handleCall} onMessage={handleSendMessage} />}
          onItemChange={handleItemChange}
          columnClassName={'h-[calc(100vh-300px)]'}
          disabled={!permission[PERMISSIONS.contact_edit]}
        />
      </Box>
      <ModalAlertConfirm
        open={openDisconnectConfirm}
        title={t('CRM.ringCentralNotConnected')}
        description={t('CRM.navigateSettingConfirmDesciption')}
        confirm={t('yes')}
        onCancel={() => {
          setOpenDisconnectConfirm(false);
        }}
        onConfirm={handleNavigateSetting}
      />
    </>
  );
};

export default CRM;
